import { useQuery } from '@apollo/client';
import { ViewComfy } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import type { LayoutsOrderBy } from '~/generated/graphql';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { NewLayoutButton } from '../components';
import { LayoutListDocument } from '../queries/list.generated';
import { Table } from './components';

export const LayoutsList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { orderBy } = useOrderByParams<LayoutsOrderBy>();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { data, loading } = useQuery(LayoutListDocument, {
    variables: { networkId: currentNetwork.id, page, perPage, search, ...(orderBy && { orderBy }) },
    onCompleted: (data) => handleOutOfBounds(data.network?.layouts.pageInfo),
  });

  const layouts = data?.network?.layouts.nodes ?? [];
  const totalCount = useTotalCount(data?.network?.layouts.pageInfo.nodesCount);

  if (!loading && !data?.network) return <Navigate to="/not-found" replace />;

  return (
    <>
      <Toolbar
        actions={<NewLayoutButton />}
        titleIcon={<ViewComfy />}
        titleText={t('pages.layouts.title')}
      />
      <ListPageContainer>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <SearchBar placeholder={t('pages.layouts.search_placeholder')} search={search} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Count
                  selectedCount={0}
                  totalCount={totalCount}
                  thing="pages.layouts.thing_plural"
                />
              </Box>
            </ListActions>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Table layouts={layouts} loading={loading} />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
        </ListContainer>
      </ListPageContainer>
    </>
  );
};
